'use client';
import LoadingLogo from '@components/Loading/LoadingLogo';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { prependSlash } from '@repo/ui/src/components/Anchor/helpers';
import { ToutContentCard } from '@repo/ui/src/components/ToutContent/_toutContentCard';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { classNames } from '@repo/ui/src/utils/classNames';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import React from 'react';
import BaseButtonWithProgress from '@components/BaseButtonWithProgress';
import NoResultsFound from './_NoResultsFound';
import { ButtonVariant } from '@repo/ui/src/components/Button';
import { ImageProps } from '@repo/ui/src/types/image';
import { ToutContentProps } from '@repo/ui/src/types/toutContent';
import { AlignH, AlignV } from '@repo/ui/src/types';

const COL_WIDTHS = [
  '',
  'md:tw-col-span-2 lg:tw-col-span-2 xl:tw-col-span-2',
  'md:tw-col-span-3 lg:tw-col-span-3 xl:tw-col-span-3',
  'md:tw-col-span-4 lg:tw-col-span-4 xl:tw-col-span-4',
];

interface NoResultsContent {
  title?: string;
  text?: string;
  image?: ImageProps;
}

interface ContentProps {
  noResultsContent?: NoResultsContent;
  [key: string]: any;
}

export interface GridLifestyleCard {
  cardIndex: number;
  cardTileWidth?: number;
  background: ImageProps;
  toutContent: ToutContentProps;
  horizontalAlignment: AlignH;
  verticalAlignment: AlignV;
  [key: string]: any;
}

interface ZRGridProps {
  items: Array<any>;
  totalPages: number;
  isLoading?: boolean;
  page: number;
  gridLifestyleCard?: GridLifestyleCard;
  viewMore: () => Promise<void>;
  content?: ContentProps;
  GridItemComponent: React.ComponentType<any>;
  hideAddToCartButton?: boolean;
  showFinancing?: boolean;
  financingText?: boolean;
  disableBorder?: boolean;
}

const ZRGrid = ({
  items,
  totalPages,
  isLoading,
  page,
  gridLifestyleCard,
  viewMore,
  content,
  GridItemComponent,
  hideAddToCartButton,
  showFinancing,
  financingText,
  disableBorder,
}: ZRGridProps) => {
  const { path, query } = useCurrentUrl();
  const { t } = useTranslation();
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);

  const disableBtn = isLoading || isLoadingMore;

  const onViewMore = async (e: React.MouseEvent<HTMLAnchorElement>): Promise<void> => {
    e.preventDefault();
    if (!disableBtn) {
      setIsLoadingMore(true);
      try {
        await viewMore();
      } catch (error) {
        console.error('Error loading more items:', error instanceof Error ? error.message : error);
      } finally {
        setIsLoadingMore(false);
      }
    }
  };

  const linkPath = React.useMemo(() => {
    const params = new URLSearchParams(query);
    params.set('paginated', 'true');
    params.set('page', (page + 1).toString());
    const basePath = prependSlash(path).replace(/\?.*/, '');
    return `${basePath}?${params.toString()}`;
  }, [path, query, page]);

  return (
    <div className="tw-w-full">
      <div className="tw-relative">
        {!items.length && (
          <NoResultsFound
            content={content?.noResultsContent}
            clearFiltersButtonLabel={t('gridContent.clearAllFiltersText')}
          />
        )}
        <ul
          className={classNames(
            'tw-grid tw-list-none tw-p-0 tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-4',
          )}
        >
          {items?.map((item, i) => (
            <React.Fragment key={i}>
              {gridLifestyleCard && i === gridLifestyleCard.cardIndex && (
                <li
                  className={classNames(
                    !disableBorder && 'tw-border tw-border-warm-200',
                    COL_WIDTHS[(gridLifestyleCard.cardTileWidth ?? 1) - 1],
                  )}
                >
                  <ToutContentCard
                    {...gridLifestyleCard}
                    className="tw-h-full tw-flex tw-items-center tw-justify-start"
                    cardBackground={gridLifestyleCard.background}
                    fullWidthAndHeight
                  />
                </li>
              )}
              <li className={classNames(!disableBorder && 'tw-border tw-border-warm-200')}>
                <GridItemComponent
                  item={item}
                  content={content}
                  hideAddToCartButton={hideAddToCartButton}
                  showFinancing={showFinancing}
                  financingText={financingText}
                />
              </li>
            </React.Fragment>
          ))}
        </ul>
        {isLoading && (
          <div className="tw-absolute tw-inset-0 tw-bg-transparent-light-500 tw-grid tw-place-content-center">
            <LoadingLogo />
          </div>
        )}
      </div>
      {(page < totalPages || isLoadingMore) && (
        <div className="tw-flex tw-justify-center tw-p-8" suppressHydrationWarning>
          <BaseButtonWithProgress
            as={Anchor}
            variant={ButtonVariant.tertiary}
            link={linkPath}
            data-type="append"
            data-page={page}
            processing={disableBtn}
            processingText={false}
            onClick={onViewMore}
          >
            {t('gridContent.viewMoreButtonText', undefined, { default: 'View More' })}
          </BaseButtonWithProgress>
        </div>
      )}
    </div>
  );
};

export default ZRGrid;
